import { ButtonToggleGroup } from 'components/ButtonToggleGroup';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { Icon, ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { Txt } from 'components/Txt';
import { useEsc } from 'hooks/useEsc';
import { filterArrayEmpty } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { sortBy, without } from 'lodash';
import React from 'react';
import { View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { doEngageSendCommunication, NAVIGATE_MODAL_BACK } from 'store/actions';
import { CommunicationType } from 'types/Engage';
import { EngageContactCard } from './Components/EngageContactCard';
import { selectEngageFlat } from 'store/remote/engageSelectors';

const CommunicationTemplates: {
  id: string;
  name: string;
  description: string;
  type: CommunicationType;
}[] = [
  {
    id: 'resident_survey_request_letter',
    name: 'Survey Request Letter',
    type: CommunicationType.letter,
    description:
      'Letter requesting information about the leaseholders and residents',
  },
  {
    id: 'resident_survey_request_email',
    name: 'Survey Request Email',
    type: CommunicationType.email,
    description:
      'Email requesting information about the leaseholders and residents',
  },
  {
    id: 'resident_survey_confirm_email',
    name: 'Survey Confirm Email',
    type: CommunicationType.email,
    description:
      "Email to request confirmation of residents and leaseholders' details",
  },
];

export const EngageSendCommunicationModal: React.FC<
  ModalScreenProps<ModalScreens.EngageSendCommunication>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const { key, contactId } = route.params;
  const { property, flat, contacts } = useSelector((state) =>
    selectEngageFlat(state, key),
  )!;

  const contact = contactId
    ? contacts.find((contact) => contact.id === contactId)
    : null;

  const [communicationId, setCommunicationId] = React.useState<string>();
  const [chosenContactIds, setChosenContactIds] = React.useState<string[]>(
    contact ? [contact.id] : [],
  );
  const [filterCommunicationTypeId, setFilterCommunicationTypeId] =
    React.useState<'email' | 'letter' | 'all'>(contact ? 'email' : 'all');

  const communicationTemplate = communicationId
    ? CommunicationTemplates.find((t) => t.id === communicationId)
    : undefined;
  const canSend =
    communicationTemplate &&
    (communicationTemplate.type === CommunicationType.letter ||
      !!chosenContactIds.length);

  const handleSend = () => {
    if (!communicationId) {
      return;
    }
    dispatch(
      doEngageSendCommunication({
        key: key,
        communicationId: communicationId,
        propertyId: property.id,
        flatId: flat.id,
        contactIds: chosenContactIds,
      }),
    );
  };

  const handleClose = React.useCallback(() => {
    if (communicationTemplate) {
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [communicationTemplate, dispatch]);

  const handleToggleContact = (contactId: string) => {
    setChosenContactIds((current) =>
      current.includes(contactId)
        ? without(current, contactId)
        : [...current, contactId],
    );
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !communicationTemplate });
  }, [navigation, communicationTemplate]);
  useEsc(handleClose);

  const contactsWithEmail = contact
    ? [contact]
    : contacts.filter((contact) => !!contact.email);
  const contactsWithoutEmail = contact
    ? []
    : contacts.filter((contact) => !contact.email);

  return (
    <PageScrollView
      header={
        <>
          <NavigationHeader
            heading="Send Message"
            onPressCancelButton={
              !communicationTemplate ? handleClose : undefined
            }
            onPressBackButton={
              communicationTemplate
                ? () => setCommunicationId(undefined)
                : undefined
            }
            onPressSaveButton={canSend ? handleSend : null}
            saveButtonText="Send"
          />
          {!communicationTemplate && (
            <ButtonToggleGroup
              buttons={[
                { text: 'All', key: 'all', disabled: !!contact },
                { text: 'Letters', key: 'letter', disabled: !!contact },
                { text: 'Emails', key: 'email' },
              ]}
              active={filterCommunicationTypeId}
              onActiveChange={(newActive) =>
                setFilterCommunicationTypeId(newActive)
              }
              containerStyle={{
                marginBottom: 12,
                alignSelf: 'stretch',
                marginHorizontal: theme.paddingHorizontal,
              }}
              rounded
              size="small"
            />
          )}
        </>
      }
      pageStyle={{ flex: 1 }}
    >
      {!communicationTemplate && (
        <Card>
          {sortBy(Object.values(CommunicationTemplates), ['ordering'])
            .filter(
              (ct) =>
                filterCommunicationTypeId === 'all' ||
                filterCommunicationTypeId === ct.type,
            )
            .map((ct) => (
              <CommunicationTemplateCardRow
                key={ct.id}
                id={ct.id}
                onPress={setCommunicationId}
              />
            ))}
        </Card>
      )}
      {!!communicationTemplate && (
        <>
          <Card
            label={
              communicationTemplate.type === CommunicationType.email
                ? 'Email'
                : 'Letter'
            }
          >
            <CommunicationTemplateCardRow id={communicationTemplate.id} />
          </Card>
          {communicationTemplate.type === CommunicationType.email && (
            <Card
              label="Send To"
              labelLinkText="Select All"
              onLabelLinkPress={
                contactsWithEmail.length > 1
                  ? () =>
                      setChosenContactIds(contactsWithEmail.map((c) => c.id))
                  : undefined
              }
              labelLinkText2="None"
              onLabelLinkPress2={
                contactsWithEmail.length > 1
                  ? () => setChosenContactIds([])
                  : undefined
              }
            >
              {!contactsWithEmail.length ? (
                <CardRow
                  label="This email cannot be sent"
                  value="There are no contacts with email addresses"
                  labelAlignTop
                />
              ) : (
                contactsWithEmail.map((thisContact) => (
                  <EngageContactCard
                    key={thisContact.id}
                    contact={thisContact}
                    checkedValue={
                      contact
                        ? undefined
                        : chosenContactIds?.includes(thisContact.id)
                    }
                    onPress={
                      contact
                        ? undefined
                        : () => handleToggleContact(thisContact.id)
                    }
                    contactDetailsOnOwnLine
                  />
                ))
              )}
            </Card>
          )}
          {communicationTemplate.type === CommunicationType.email &&
            !!contactsWithoutEmail.length && (
              <Card
                label="Cannot Send To"
                explanation="These contacts do not have email addresses"
              >
                {contactsWithoutEmail.map((contact) => (
                  <EngageContactCard
                    key={contact.id}
                    contact={contact}
                    contactDetailsOnOwnLine
                  />
                ))}
              </Card>
            )}
          {communicationTemplate.type === CommunicationType.letter && (
            <Card label="Send To">
              <CardRow
                childrenStyle={{
                  flexDirection: 'row',
                  columnGap: theme.paddingHorizontal,
                  alignItems: 'flex-start',
                }}
              >
                <View
                  style={{
                    borderRadius: 42,
                    width: 42,
                    height: 42,
                    backgroundColor: theme.colors.black10,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon source={ICONS.HouseBuildingLight} />
                </View>
                <View style={{ flex: 1 }}>
                  <Txt>
                    {[flat.name, ...property.addressLines, property.postcode]
                      .filter(filterArrayEmpty)
                      .join('\n')}
                  </Txt>
                </View>
              </CardRow>
            </Card>
          )}
        </>
      )}
    </PageScrollView>
  );
};

const CommunicationTemplateCardRow: React.FC<{
  id: string;
  onPress?: (id: string) => void;
}> = ({ id, onPress }) => {
  const communicationTemplate = CommunicationTemplates.find(
    (c) => c.id === id,
  )!;
  return (
    <CardRow
      key={communicationTemplate.id}
      childrenStyle={{
        flexDirection: 'row',
        columnGap: theme.paddingHorizontal,
        alignItems: 'center',
      }}
      onPress={onPress ? () => onPress(communicationTemplate.id) : undefined}
    >
      <View
        style={{
          borderRadius: 42,
          width: 42,
          height: 42,
          backgroundColor: theme.colors.black10,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {communicationTemplate.type === CommunicationType.email ? (
          <Icon source={ICONS.AtLight} />
        ) : (
          <Icon source={ICONS.FileLight} />
        )}
      </View>
      <View style={{ flex: 1 }}>
        <Txt>{communicationTemplate.name}</Txt>
        {!!communicationTemplate.description && (
          <Txt size="xxsmall" style={{ color: theme.colors.fieldValueText }}>
            {communicationTemplate.description}
          </Txt>
        )}
      </View>
    </CardRow>
  );
};
